import pluralize from '@src/utils/pluralize';
import {
  TRANSLATE_CREDIT_PER_CHAR,
  REWRITE_CREDIT_PER_CHAR,
} from '@src/constants/project';
import { NARRATION_MODELS } from '@src/constants/presentation';

const findModelCredit = (models, modelValue) => {
  if (!Array.isArray(models)) return null;
  const model = models.find((m) => m?.value === modelValue);
  return model.creditPerSlide;
};

export const buildAPIRows = ({
  t,
  language,
  ttsAsyncConfig,
  ttsSyncConfig,
  ttsStreamingConfig,
  sttAsyncConfig,
  sttSyncConfig,
  sttStreamingConfig,
}) => {
  const formatCostPerChar = (creditPerChar) => {
    if (!creditPerChar) return null;
    const creditUnit = pluralize(creditPerChar, t('credit'), language);
    return t('costPerCharacterUnit', {
      cost: creditPerChar.toLocaleString(language),
      creditUnit,
    });
  };

  const formatCostPerBlock = (creditPerBlock) => {
    if (!creditPerBlock) return null;
    const creditUnit = pluralize(creditPerBlock, t('credit'), language);
    return t('costPerBlockUnit', {
      cost: creditPerBlock.toLocaleString(language),
      creditUnit,
    });
  };

  const formatSttDescription = (blockDuration) =>
    t('costPerBlock', { blockDuration: blockDuration ?? '' });

  return [
    {
      key: 'tts-batch',
      name: t('ttsBatch'),
      description: t('newCreditIntroTTSDescription'),
      cost: formatCostPerChar(ttsAsyncConfig?.creditPerChar),
      hidden: !ttsAsyncConfig?.creditPerChar,
    },
    {
      key: 'tts-realtime',
      name: t('ttsRealtime'),
      description: t('newCreditIntroTTSDescription'),
      cost: formatCostPerChar(ttsSyncConfig?.creditPerChar),
      hidden: !ttsSyncConfig?.creditPerChar,
    },
    {
      key: 'tts-streaming',
      name: t('ttsStreaming'),
      description: t('newCreditIntroTTSDescription'),
      cost: formatCostPerChar(ttsStreamingConfig?.creditPerChar),
      hidden: !ttsStreamingConfig?.creditPerChar,
    },
    {
      key: 'stt-batch',
      name: t('sttBatch'),
      description: formatSttDescription(sttAsyncConfig?.blockDuration),
      cost: formatCostPerBlock(sttAsyncConfig?.creditPerBlock),
      hidden: !sttAsyncConfig?.creditPerBlock,
    },
    {
      key: 'stt-realtime',
      name: t('sttRealtime'),
      description: formatSttDescription(sttSyncConfig?.blockDuration),
      cost: formatCostPerBlock(sttSyncConfig?.creditPerBlock),
      hidden: !sttSyncConfig?.creditPerBlock,
    },
    {
      key: 'stt-streaming',
      name: t('sttStreaming'),
      description: formatSttDescription(sttStreamingConfig?.blockDuration),
      cost: formatCostPerBlock(sttStreamingConfig?.creditPerBlock),
      hidden: !sttStreamingConfig?.creditPerBlock,
    },
  ];
};

export const buildAIRows = ({
  t,
  language,
  aiTranslateEnabled,
  aiRewriteEnabled,
  aiPresentationEnabled,
  presentationAIModels,
  transcriptAudioEnabled,
  transcriptAudioModels,
}) => {
  const formatCostPerChar = (creditPerChar) => {
    const creditUnit = pluralize(creditPerChar, t('credit'), language);
    return t('costPerCharacterUnit', {
      cost: creditPerChar.toLocaleString(language),
      creditUnit,
    });
  };

  const formatCostPerSlide = (creditPerSlide) => {
    if (!creditPerSlide) return null;
    const creditUnit = pluralize(creditPerSlide, t('credit'), language);
    return `${creditPerSlide.toLocaleString(language)} ${creditUnit}/slide`;
  };

  const formatCostPerSecond = (creditPerSecond) => {
    if (!creditPerSecond) return null;
    const creditUnit = pluralize(creditPerSecond, t('credit'), language);
    return `${creditPerSecond.toLocaleString(language)} ${creditUnit}/${t(
      'second',
    ).toLowerCase()}`;
  };

  const getModelDescription = (model) => {
    const description = model?.description || {};
    return description[language] || description.vi || description.en || '';
  };

  const flashCredit = findModelCredit(
    presentationAIModels,
    NARRATION_MODELS.FLASH,
  );
  const proCredit = findModelCredit(presentationAIModels, NARRATION_MODELS.PRO);

  return [
    {
      key: 'pronunciation-check',
      name: t('newCreditIntroPronunciationCheck'),
      description: t('newCreditIntroPronunciationCheckDescription'),
      cost: `0 ${pluralize(0, t('credit'), language)}`,
      hidden: false,
    },
    {
      key: 'translate',
      name: t('translate'),
      description: t('newCreditIntroTranslateDescription'),
      cost: formatCostPerChar(TRANSLATE_CREDIT_PER_CHAR),
      hidden: !aiTranslateEnabled,
    },
    {
      key: 'rewrite',
      name: t('rewrite'),
      description: t('newCreditIntroRewriteDescription'),
      cost: formatCostPerChar(REWRITE_CREDIT_PER_CHAR),
      hidden: !aiRewriteEnabled,
    },
    {
      key: 'narration',
      name: t('newCreditIntroNarration'),
      description: t('newCreditIntroNarrationDescription'),
      cost: t('newCreditIntroDependOnModel'),
      isCostItalic: true,
      hidden: !aiPresentationEnabled,
    },
    {
      key: 'narration-flash',
      name: t('newCreditIntroNarrationModelName', {
        model: NARRATION_MODELS.FLASH,
      }),
      description: t('newCreditIntroNarrationFlashDescription'),
      cost: formatCostPerSlide(flashCredit),
      isSubRow: true,
      hidden: !aiPresentationEnabled || !flashCredit,
    },
    {
      key: 'narration-pro',
      name: t('newCreditIntroNarrationModelName', {
        model: NARRATION_MODELS.PRO,
      }),
      description: t('newCreditIntroNarrationProDescription'),
      cost: formatCostPerSlide(proCredit),
      isSubRow: true,
      hidden: !aiPresentationEnabled || !proCredit,
    },
    {
      key: 'transcript-audio',
      name: t('transcriptAudio'),
      description: t('newCreditIntroTranscriptAudioDescription'),
      cost: t('newCreditIntroDependOnModel'),
      isCostItalic: true,
      hidden: !transcriptAudioEnabled,
    },
    ...(Array.isArray(transcriptAudioModels) ? transcriptAudioModels : [])
      .filter((model) => model?.model)
      .map((model) => ({
        key: `transcript-audio-${model.model}`,
        name: t('newCreditIntroNarrationModelName', { model: model.model }),
        description: getModelDescription(model),
        cost: formatCostPerSecond(model.creditPerSecond),
        isSubRow: true,
        hidden: !transcriptAudioEnabled,
      })),
  ];
};
